import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { ticketType } from "@agentcall/core/type"
import { format } from "date-fns"
import { Edit } from "react-feather"
import type { TicketModels } from "@/api/client/ticket"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { openInNewTab } from "@/lib/navigation"

interface TicketInfoTabProps {
  ticket: TicketModels.TicketGet200Ticket
}

export function TicketInfoTab({ ticket }: TicketInfoTabProps) {
  const { canUpdate } = usePermission()
  const canEditTicket = canUpdate("tickets")

  return (
    <div className="mt-4 space-y-4">
      {/* ステータスと編集ボタン */}
      <div className="flex items-end justify-between">
        <Card className="inline-flex p-5 gap-2.5">
          <div className="flex items-center gap-2.5">
            <span className="text-sm text-[#3f4652] font-medium">案件ステータス</span>
            <Label variant="oc">{getStatusLabel(ticket.status)}</Label>
          </div>
        </Card>
        {canEditTicket && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-[#354267] text-[#354267]"
            onClick={() => openInNewTab(`/tickets/${ticket.id}/edit`)}
          >
            <Edit size={14} />
            編集
          </Button>
        )}
      </div>

      {/* 案件管理情報 */}
      <Card className="p-0">
        <div className="bg-white border-b border-slate-200 px-5 py-2.5 h-[52px] flex items-center rounded-t-[10px]">
          <h3 className="text-sm font-semibold text-[#3f4652]">案件管理情報</h3>
        </div>
        <div className="flex">
          <div className="flex-1">
            <InfoRow label="案件タイトル" value={ticket.title || "-"} />
            <InfoRow
              label="案件カテゴリ"
              value={getCategoryLabel(ticket.category, ticket.subCategory)}
            />
            <InfoRow label="OC担当者" value={ticket.ocAssignee?.name || "-"} />
            <InfoRow label="OCコメント" value={ticket.ocComment || "-"} isLast />
          </div>
          <div className="flex-1">
            <InfoRow label="案件番号" value={String(ticket.id) || "-"} />
            <InfoRow
              label="発生日時"
              value={
                ticket.createdAt ? format(new Date(ticket.createdAt), "yyyy-MM-dd HH:mm") : "-"
              }
            />
            <InfoRow
              label="アラート日時"
              value={ticket.alertAt ? format(new Date(ticket.alertAt), "yyyy-MM-dd HH:mm") : "-"}
            />
            <InfoRow label="アラートコメント" value={ticket.alertComment || "-"} isLast />
          </div>
        </div>
      </Card>

      {/* 契約情報 */}
      <Card className="p-0">
        <div className="bg-white border-b border-slate-200 px-5 py-2.5 h-[52px] flex items-center justify-between rounded-t-[10px]">
          <h3 className="text-sm font-semibold text-[#3f4652]">契約情報</h3>
          {/* TODO: フェーズ2 */}
          {/* <div className="flex gap-2.5">
            <Button
              size="sm"
              className="bg-[#354267] hover:bg-[#354267]/90 text-white font-bold text-sm"
              onClick={() => navigate("/tickets")}
            >
              契約の案件表示
            </Button>
            <Button
              size="sm"
              className="bg-[#354267] hover:bg-[#354267]/90 text-white font-bold text-sm"
              onClick={() => navigate("/tickets")}
            >
              物件の案件表示
            </Button>
          </div> */}
        </div>
        <div className="flex">
          <div className="flex-1">
            <InfoRowWithLink
              label="物件名"
              value={ticket.property?.name || "-"}
              linkTo={ticket.property?.id ? `/properties/${ticket.property.id}` : undefined}
            />
            <InfoRowWithLink
              label="賃借人"
              value={ticket.tenant?.name || "-"}
              linkTo={ticket.tenant?.id ? `/tenants/${ticket.tenant.id}` : undefined}
            />
            <InfoRow label="加入プラン" value="-" />
            <InfoRow label="契約期間" value="-" />
            <InfoRow label="物件注意事項" value={ticket.property?.note || "-"} isLast />
          </div>
          <div className="flex-1">
            <InfoRowWithLink
              label="居室番号"
              value={ticket.room?.name || "-"}
              linkTo={
                ticket.property?.id && ticket.room?.id
                  ? `/properties/${ticket.property.id}/rooms/${ticket.room.id}`
                  : undefined
              }
            />
            <InfoRow
              label="電話番号"
              value={
                ticket.tenant
                  ? formatPhone(ticket.tenant.phone1, ticket.tenant.phone2, ticket.tenant.phone3)
                  : "-"
              }
            />
            <InfoRowWithLink
              label="オーナー名"
              value={ticket.owner?.name || "-"}
              linkTo={ticket.owner?.id ? `/owners/${ticket.owner.id}` : undefined}
            />
            <InfoRow label="オーナー電話番号" value={ticket.owner?.phoneNumber || "-"} />
            <InfoRow label="入居者注意事項" value={ticket.tenant?.adminMemo || "-"} isLast />
          </div>
        </div>
      </Card>

      {/* 業者依頼情報 */}
      <Card className="p-0">
        <div className="bg-white border-b border-slate-200 px-5 py-2.5 h-[52px] flex items-center rounded-t-[10px]">
          <h3 className="text-sm font-semibold text-[#3f4652]">業者依頼情報</h3>
        </div>
        <div className="flex">
          <div className="flex-1">
            <InfoRow
              label="業者対応状況"
              value={getContractorStatusLabel(ticket.contractorStatus)}
            />
            <InfoRow label="業者名" value={ticket.contractorName || "-"} />
            <InfoRow
              label="業者対応予定日"
              value={
                ticket.contractorScheduledDate
                  ? format(new Date(ticket.contractorScheduledDate), "yyyy-MM-dd HH:mm")
                  : "-"
              }
              isLast
            />
          </div>
          <div className="flex-1">
            <InfoRow label="手配先業者" value={getContractorTypeLabel(ticket.contractorType)} />
            <InfoRow label="業者担当者" value={ticket.contractorContact || "-"} />
            <InfoRow label="業者連絡先" value={ticket.contractorContactInfo || "-"} />
            <InfoRow label="備考" value={ticket.contractorNote || "-"} isLast />
          </div>
        </div>
      </Card>
    </div>
  )
}

interface InfoRowProps {
  label: string
  value: string
  isLast?: boolean
}

function InfoRow({ label, value, isLast }: InfoRowProps) {
  return (
    <div className={`flex h-[34px] items-center ${!isLast ? "border-b border-slate-200" : ""}`}>
      <div className="bg-slate-50 h-full flex items-center px-5 w-[180px]">
        <span className="text-sm text-[#3f4652] font-medium">{label}</span>
      </div>
      <div className="flex-1 px-5 flex items-center h-full">
        <span className="text-sm text-[#3f4652]">{value}</span>
      </div>
    </div>
  )
}

interface InfoRowWithLinkProps {
  label: string
  value: string
  linkTo?: string
  isLast?: boolean
}

function InfoRowWithLink({ label, value, linkTo, isLast }: InfoRowWithLinkProps) {
  const handleClick = () => {
    if (linkTo) {
      openInNewTab(linkTo)
    }
  }

  return (
    <div className={`flex h-[34px] items-center ${!isLast ? "border-b border-slate-200" : ""}`}>
      <div className="bg-slate-50 h-full flex items-center px-5 w-[180px]">
        <span className="text-sm text-[#3f4652] font-medium">{label}</span>
      </div>
      <div className="flex-1 px-5 flex items-center h-full">
        {linkTo ? (
          <span
            className="text-sm text-[#458CF7] cursor-pointer hover:opacity-80"
            onClick={handleClick}
          >
            {value}
          </span>
        ) : (
          <span className="text-sm text-[#3f4652]">{value}</span>
        )}
      </div>
    </div>
  )
}

// Helper functions
function getStatusLabel(status?: TicketModels.TicketGet200TicketStatus): string {
  if (!status) return "-"
  const found = ticketType.TICKET_STATUSES.find(
    (s: (typeof ticketType.TICKET_STATUSES)[number]) => s.key === status
  )
  return found?.value ?? "-"
}

function getContractorStatusLabel(
  status?: TicketModels.TicketGet200TicketContractorStatus
): string {
  if (!status) return "-"
  const found = ticketType.CONTRACTOR_STATUSES.find(
    (s: (typeof ticketType.CONTRACTOR_STATUSES)[number]) => s.key === status
  )
  return found?.value ?? "-"
}

function getContractorTypeLabel(type?: TicketModels.TicketGet200TicketContractorType): string {
  if (!type) return "-"
  const found = ticketType.CONTRACTOR_TYPES.find(
    (t: (typeof ticketType.CONTRACTOR_TYPES)[number]) => t.key === type
  )
  return found?.value ?? "-"
}

function getCategoryLabel(
  category?: TicketModels.TicketGet200TicketCategory,
  subCategory?: TicketModels.TicketGet200TicketSubCategory
): string {
  const categoryLabel = category
    ? (ticketType.TICKET_CATEGORIES.find(
        (c: (typeof ticketType.TICKET_CATEGORIES)[number]) => c.key === category
      )?.value ?? "")
    : ""
  const subCategoryLabel = subCategory
    ? (ticketType.TICKET_SUBCATEGORIES.find(
        (s: (typeof ticketType.TICKET_SUBCATEGORIES)[number]) => s.key === subCategory
      )?.value ?? "")
    : ""

  if (categoryLabel && subCategoryLabel) {
    return `${categoryLabel} / ${subCategoryLabel}`
  }
  return categoryLabel || "-"
}
